import type { Resume } from "@adafia/types";
import {
	closestCenter,
	DndContext,
	type DragEndEvent,
	type SensorDescriptor,
	type SensorOptions,
} from "@dnd-kit/core";
import {
	restrictToFirstScrollableAncestor,
	restrictToVerticalAxis,
} from "@dnd-kit/modifiers";
import {
	SortableContext,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Folder } from "lucide-react";
import { InputGroup } from "./InputGroup";
import { SectionHeader } from "./SectionHeader";
import { SortableItem } from "./SortableItem";
import { TextAreaGroup } from "./TextAreaGroup";

interface ProjectEditorProps {
	projects: Resume["projects"];
	onAdd: () => void;
	onUpdate: (id: string, field: string, value: string) => void;
	onRemove: (id: string) => void;
	onDragEnd: (event: DragEndEvent) => void;
	sensors: SensorDescriptor<SensorOptions>[];
}

export function ProjectEditor({
	projects,
	onAdd,
	onUpdate,
	onRemove,
	onDragEnd,
	sensors,
}: ProjectEditorProps) {
	return (
		<>
			<SectionHeader title="Projects" icon={Folder} onAdd={onAdd} />
			<DndContext
				sensors={sensors}
				collisionDetection={closestCenter}
				onDragEnd={onDragEnd}
				modifiers={[restrictToVerticalAxis, restrictToFirstScrollableAncestor]}
			>
				<SortableContext
					items={(projects || []).map((p) => p.id)}
					strategy={verticalListSortingStrategy}
				>
					{(projects || []).map((project) => (
						<SortableItem
							key={project.id}
							id={project.id}
							onDelete={() => onRemove(project.id)}
						>
							<InputGroup
								label="Project Name"
								value={project.name}
								onChange={(v) => onUpdate(project.id, "name", v)}
							/>
							<InputGroup
								label="Project Link"
								value={project.link || ""}
								onChange={(v) => onUpdate(project.id, "link", v)}
							/>
							<TextAreaGroup
								label="Description"
								value={project.description}
								onChange={(v) => onUpdate(project.id, "description", v)}
							/>
						</SortableItem>
					))}
				</SortableContext>
			</DndContext>
		</>
	);
}
